'use client';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import { RefObject } from 'react';

interface FilterSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  className?: string;
  /** Extra classes on the input itself — `className` styles the wrapper. */
  inputClassName?: string;
  /** Accessible name when the surrounding row gives no visible label. */
  ariaLabel?: string;
  /**
   * Hover text on the input. Use it to advertise the key that focuses the field
   * (`"Search contacts · /"`), the way the surface header's search button does —
   * a shortcut nothing on screen names is a shortcut nobody presses.
   */
  title?: string;
  disabled?: boolean;
  /** `compact` — h-8 + 13px text, aligned with HubTable hub chrome */
  size?: 'default' | 'compact';
}

/**
 * FilterSearch Component
 * Standardized search input for filter bars
 * Consistent styling: h-9, border-slate-200, shadow-sm with search icon
 */
export const FilterSearch: React.FC<FilterSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search',
  inputRef,
  className = '',
  inputClassName,
  ariaLabel,
  title,
  disabled = false,
  size = 'default',
}) => {
  const compact = size === 'compact';

  return (
    <div className={`relative ${className}`}>
      <Search className={cn(
        'absolute top-0 bottom-0 my-auto text-slate-400',
        compact ? 'left-2.5 h-3.5 w-3.5' : 'left-3 h-4 w-4',
      )} />
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        title={title}
        disabled={disabled}
        className={cn(
          'border-slate-200 bg-white shadow-sm focus-visible:ring-2 focus-visible:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100',
          compact ? 'h-8 pl-8 text-[13px]' : 'h-9 pl-9',
          inputClassName,
        )}
      />
    </div>
  );
};
